import React from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { CTAButton } from "../home/blocks/odyssee/OdysseeCard";
import { useSelector } from "react-redux";
import { getUser } from "../../store/reducers/authSlice";
import { calculateProductPrice } from "../basket/BasketProductCard";
import InexButton from "../InexButton";
import { ShoppingBagOutlined } from "@mui/icons-material";

const SubscribeButton = ({ subscriptionKey = undefined, isSubscribed }) => {
	const indexesInUrl = React.useMemo(() => {
		switch (subscriptionKey) {
			case "digital":
				return [1];
			case "tv":
				return [2];
			case "mag":
				return [3];
			default:
				return [2, 3];
		}
	}, [subscriptionKey]);

	return (
		<a
			href={
				!isSubscribed
					? `https://abonnements.inexplore.com/step/subscription?selectedAbos=[${indexesInUrl}]`
					: "#"
			}
			aria-disabled={isSubscribed}
		>
			<CTAButton
				text={isSubscribed ? "Déjà abonné" : "Je m'abonne"}
				textWithGradient={false}
				disableBackground={true}
				disabled={isSubscribed}
				sx={{
					flexDirection: "row-reverse",
					color: (theme) => (!isSubscribed ? theme.palette.secondary.main : theme.palette.grey[500]),
					backgroundColor: (theme) => (!isSubscribed ? theme.palette.yellow : theme.palette.grey[500]),
					"& > p": {
						backgroundColor: "transparent",
					},
				}}
			/>
		</a>
	);
};

const EventReductionsDetailDialog = ({ open, onClose, product }) => {
	const user = useSelector(getUser);
	const [totalPrice, totalPromo, totalPriceBeforePromo] = React.useMemo(() => {
		if (!product) return [0, 0, 0];
		return calculateProductPrice(product, user);
	}, [product, user]);

	const maxPromo = React.useMemo(() => {
		const idx = ["reduct_tv", "reduct_mag"];
		return idx.reduce((acc, key) => acc + (product?.[key] ?? 0), 0);
	}, [product]);

	let rows = [];
	const nowverif = new Date().getTime() / 1000;
	if (!user || (!(user["digital"] > nowverif) && !(user["tv"] > nowverif) && !(user["mag"] > nowverif))) {
		rows = [
			{
				name: "Inexploré TV + digital + magazine",
				subKey: undefined,
				key: undefined,
				price: 12.9,
				promo: maxPromo,
				mostInteresting: true,
			},
			{
				name: "Inexploré TV + digital",
				subKey: "tv",
				key: "reduct_tv",
				price: 9.9,
				promo: product?.reduct_tv,
			},
			{
				name: "Inexploré Magazine",
				subKey: "mag",
				key: "reduct_mag",
				price: 3.0,
				promo: product?.reduct_mag,
			},
			{
				name: "Inexploré digital",
				subKey: "digital",
				key: "reduct_digit",
				price: 2.9,
				promo: product?.reduct_digit,
			},
		];
	} else {
		if (!(user["tv"] > nowverif)) {
			if (!(user["mag"] > nowverif)) {
				rows.push({
					name: "Inexploré TV + digital + magazine",
					subKey: undefined,
					key: undefined,
					price: 12.9,
					promo: maxPromo,
				});
				rows.push({
					name: "Inexploré TV + digital",
					subKey: "tv",
					key: "reduct_tv",
					price: 9.9,
					promo: product?.reduct_tv,
				});
				rows.push({
					name: "Inexploré magazine",
					subKey: "mag",
					key: "reduct_mag",
					price: 3.0,
					promo:
						user["digital"] > nowverif ? product?.reduct_digit + product?.reduct_mag : product?.reduct_mag,
				});
			} else {
				rows.push({
					name: "Inexploré TV + digital",
					subKey: "tv",
					key: "reduct_tv",
					price: 9.9,
					promo: product?.reduct_tv + product?.reduct_mag,
				});
				if (!(user["digital"] > nowverif)) {
					rows.push({
						name: "Inexploré digital",
						subKey: "digital",
						key: "reduct_digit",
						price: 2.9,
						promo: product?.reduct_digit + product?.reduct_mag,
					});
				}
			}
		} else {
			if (!(user["mag"] > nowverif)) {
				rows.push({
					name: "Inexploré magazine",
					subKey: "mag",
					key: "reduct_mag",
					price: 3.0,
					promo: product?.reduct_mag,
				});
			}
		}
	}

	return (
		<Dialog
			open={open}
			maxWidth="lg"
			onClose={() => {
				onClose(false);
			}}
		>
			<DialogTitle>Bénéficiez de réductions grâce à nos abonnements !</DialogTitle>
			<DialogContent>
				<Table sx={{ minWidth: 650 }} aria-label="simple table">
					<TableHead>
						<TableRow>
							<TableCell>Abonnement</TableCell>
							<TableCell align="right">Réduction</TableCell>
							<TableCell align="right">Nouveau prix</TableCell>
							<TableCell align="center"></TableCell>
						</TableRow>
					</TableHead>
					<TableBody
						sx={{
							fontFamily: "Branding Medium",
						}}
					>
						<TableRow
							key={"noPromo"}
							sx={{
								"&:last-child td, &:last-child th": {
									border: 0,
								},
							}}
						>
							<TableCell
								component="th"
								scope="row"
								sx={{
									fontFamily: "Branding SemiBold",
								}}
							>
								<p>{`Aucun abonnement inexploré`}</p>
							</TableCell>
							<TableCell align="right">{`Aucune réduction`}</TableCell>
							<TableCell
								align="right"
								sx={{
									color: (theme) => theme.palette.primary.main,
									fontFamily: "Branding Bold",
								}}
							>{`${totalPriceBeforePromo.toFixed(2).replace(".", ",")}€`}</TableCell>
							<TableCell align="center">
								<a href={product ? `/panier?id=${product.id}&quantity=${1}` : "#"}>
									<CTAButton
										variant={"texticon"}
										icon={
											<ShoppingBagOutlined
												sx={{
													fontSize: "26px",
													color: (theme) =>
														product
															? theme.palette.secondary.main
															: theme.palette.grey[300],
												}}
											/>
										}
										textWithGradient={false}
										disableBackground={true}
										text={"Ajouter au panier"}
										disabled={!product}
										sx={{
											padding: 3,
											marginRight: 2,
											flexDirection: "row-reverse",
											color: (theme) => theme.palette.secondary.main,
											backgroundColor: (theme) =>
												product ? theme.palette.yellow : theme.palette.grey[500],
											"& > p": {
												backgroundColor: "transparent",
												color: (theme) => (product ? undefined : theme.palette.grey[300]),
											},
										}}
									/>
								</a>
							</TableCell>
						</TableRow>
						{rows.map((row) => {
							const prixAfterPromo = product.prix - (row.key ? row.promo : maxPromo);
							//const promo = row.key ? product[row.key] : maxPromo;
							const promo = row.key ? row.promo : maxPromo;

							const now = new Date().getTime() / 1000;
							const isSubscribed = !user ? false : user[row.subKey] > now;
							const isSubToAll = !user ? false : user["tv"] > now && user["mag"] > now;

							return (
								<TableRow
									key={row.name}
									sx={{
										background: (theme) =>
											isSubscribed || isSubToAll ? theme.palette.grey[200] : undefined,
										"&:last-child td, &:last-child th": {
											border: 0,
										},
										border: (theme) =>
											row.mostInteresting ? `${theme.palette.yellow} solid 2px` : undefined,
										position: "relative",
										paddingTop: (theme) => theme.spacing(2),
										paddingBottom: (theme) => theme.spacing(2),
									}}
								>
									<TableCell
										component="th"
										scope="row"
										sx={{
											fontFamily: "Branding SemiBold",
										}}
									>
										{row.name} <p>{`Pour seulement ${row.price} € en plus par mois`} </p>
									</TableCell>
									<TableCell align="right">{`${(promo / 100).toFixed(2).replace(".", ",")}€`}</TableCell>
									<TableCell
										align="right"
										sx={{
											color: (theme) => theme.palette.primary.main,
											fontFamily: "Branding Bold",
										}}
									>{`${(prixAfterPromo / 100).toFixed(2).replace(".", ",")}€`}</TableCell>
									<TableCell align="center">
										<SubscribeButton
											subscriptionKey={row.subKey}
											isSubscribed={isSubscribed || isSubToAll}
										/>
									</TableCell>
									{row.mostInteresting && (
										<Typography
											variant={"caption"}
											sx={{
												position: "absolute",
												top: "-20px",
												left: "20px",
												background: (theme) => theme.palette.yellow,
												padding: (theme) => `${theme.spacing(1)} ${theme.spacing(2)}`,
												borderRadius: "20px",
											}}
										>
											Offre la plus intéressante
										</Typography>
									)}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={() => {
						onClose(false);
					}}
				>
					Fermer
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default EventReductionsDetailDialog;
